import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { componentOptions, type ComponentName } from "app/componentOptions";

const screenRouteKey = "__jmScreenRoute";
const mountedOwners = new Map<string, string>();
const launchQueryKeys = new Set([
  "tenant",
  "persist",
  "action",
  "tab",
  "tabs",
  "initialTab",
  "initialPath",
  "context",
]);

export type ScreenRouteLocation = {
  initialTab?: string;
  initialPath: string[];
  query?: Record<string, string>;
};

type ScreenRouteState = { ownerId: string; routes: string[]; baseHref: string };

function getScreenRouteState(state: unknown): ScreenRouteState | null {
  if (!state || typeof state !== "object" || !(screenRouteKey in state))
    return null;
  const route = state[screenRouteKey];
  if (
    !route ||
    typeof route !== "object" ||
    !("ownerId" in route) ||
    typeof route.ownerId !== "string" ||
    !("routes" in route) ||
    !Array.isArray(route.routes) ||
    !route.routes.every((href): href is string => typeof href === "string")
  )
    return null;
  return {
    ownerId: route.ownerId,
    routes: route.routes,
    baseHref:
      "baseHref" in route && typeof route.baseHref === "string"
        ? route.baseHref
        : (route.routes[0] ?? "/"),
  };
}

export const getScreenRouteOwnerId = (state: unknown) => {
  const ownerId = getScreenRouteState(state)?.ownerId;
  return ownerId ? (mountedOwners.get(ownerId) ?? ownerId) : null;
};
export const getScreenRouteBaseHref = (state: unknown) =>
  getScreenRouteState(state)?.baseHref ?? null;
export const getScreenRouteBackSteps = (state: unknown, ownerId: string) => {
  const route = getScreenRouteState(state);
  return route && getScreenRouteOwnerId(state) === ownerId
    ? Math.max(1, route.routes.length)
    : 1;
};
export const hasMountedScreenRoute = (state: unknown) => {
  const ownerId = getScreenRouteState(state)?.ownerId;
  return ownerId !== undefined && mountedOwners.has(ownerId);
};

export const ScreenRouteContext = createContext<{
  ownerId: string;
  location: ScreenRouteLocation;
  navigate: (
    location: ScreenRouteLocation,
    options?: { replace?: boolean },
  ) => void;
} | null>(null);

export const ScreenPathContext = createContext<{
  initialPath: string[];
  onPathChange: (path: string[]) => void;
} | null>(null);

export const useScreenRoute = () => useContext(ScreenRouteContext);

function readLocation(
  componentName: ComponentName,
  props: unknown,
): ScreenRouteLocation {
  if (!props || typeof props !== "object") return { initialPath: [] };
  const parts = (
    componentOptions[componentName].routing?.getPathParts?.({
      componentProps: { ...props, initialTab: undefined, initialPath: [] },
    }) ?? []
  )
    .filter((part) => part !== undefined && part !== null && part !== "")
    .map(String);
  // A new mobile screen can mount before its URL replaces the outgoing screen.
  let ownsUrl = false;
  try {
    const [urlComponent, ...urlParts] = window.location.pathname
      .split("/")
      .filter(Boolean)
      .map(decodeURIComponent);
    ownsUrl =
      urlComponent?.toLowerCase() === componentName.toLowerCase() &&
      parts.every((part, index) => part === urlParts[index]);
  } catch {
    // A malformed outgoing URL cannot supply this screen's navigation.
  }
  const query = ownsUrl
    ? Object.fromEntries(
        [...new URLSearchParams(window.location.search)].filter(
          ([key]) => !launchQueryKeys.has(key),
        ),
      )
    : {};
  return {
    ...(Object.keys(query).length ? { query } : {}),
    initialTab:
      "initialTab" in props && typeof props.initialTab === "string"
        ? props.initialTab
        : undefined,
    initialPath:
      "initialPath" in props && Array.isArray(props.initialPath)
        ? props.initialPath.filter(
            (part): part is string => typeof part === "string",
          )
        : [],
  };
}

export function ScreenRouteProvider({
  ownerId,
  componentName,
  componentProps,
  trackHistory,
  children,
}: PropsWithChildren<{
  ownerId: string;
  componentName: ComponentName;
  componentProps?: object;
  trackHistory: boolean;
}>) {
  const [location, setLocation] = useState(() =>
    readLocation(componentName, componentProps),
  );
  const currentLocation = useRef(location);
  const routeOwnerId = useRef(ownerId);
  currentLocation.current = location;
  const supportsRouting =
    componentName === "RecordScreen" || componentName === "AutoScreen";
  const getHref = useCallback(
    (next: ScreenRouteLocation) => {
      const parts =
        componentOptions[componentName].routing?.getPathParts?.({
          componentProps: { ...componentProps, ...next },
        }) ?? [];
      const url = new URL(window.location.href);
      url.pathname = [
        "",
        componentName,
        ...parts.filter(
          (part) => part !== undefined && part !== null && part !== "",
        ),
      ]
        .map((part) => encodeURIComponent(String(part)))
        .join("/");
      // Navigation URLs retain tenancy, but not one-shot launch directives.
      const tenant = url.searchParams.get("tenant");
      url.search = "";
      if (tenant) url.searchParams.set("tenant", tenant);
      for (const [key, value] of Object.entries(next.query ?? {})) {
        if (!launchQueryKeys.has(key)) url.searchParams.set(key, value);
      }
      return `${url.pathname}${url.search}${url.hash}`;
    },
    [componentName, componentProps],
  );

  useEffect(() => {
    if (!supportsRouting || !trackHistory) return;
    const existing = getScreenRouteState(window.history.state);
    if (
      existing &&
      !mountedOwners.has(existing.ownerId) &&
      getHref(currentLocation.current) ===
        `${window.location.pathname}${window.location.search}${window.location.hash}`
    ) {
      // Keep the persisted identity so earlier entries still restore after reload.
      routeOwnerId.current = existing.ownerId;
    }
    const persistedOwnerId = routeOwnerId.current;
    mountedOwners.set(persistedOwnerId, ownerId);
    const restore = (event: PopStateEvent) => {
      if (getScreenRouteOwnerId(event.state) !== ownerId) return;
      const parts = window.location.pathname
        .split("/")
        .filter(Boolean)
        .map(decodeURIComponent);
      if (parts.length === 0) {
        setLocation(readLocation(componentName, componentProps));
        return;
      }
      if (parts.shift() !== componentName) return;
      const resolved = componentOptions[componentName].routing?.resolve?.({
        pathParts: parts,
        searchParams: new URLSearchParams(window.location.search),
      });
      if (resolved)
        setLocation(readLocation(componentName, resolved.componentProps));
    };
    window.addEventListener("popstate", restore);
    return () => {
      mountedOwners.delete(persistedOwnerId);
      window.removeEventListener("popstate", restore);
    };
  }, [
    componentName,
    componentProps,
    getHref,
    ownerId,
    supportsRouting,
    trackHistory,
  ]);

  const navigate = useCallback(
    (next: ScreenRouteLocation, options?: { replace?: boolean }) => {
      if (!supportsRouting) return;
      const previous = currentLocation.current;
      if (next.query === undefined && previous.query) {
        next = { ...next, query: previous.query };
      }
      if (!trackHistory) {
        currentLocation.current = next;
        setLocation(next);
        return;
      }
      const href = getHref(next);
      const previousHref = getHref({
        ...previous,
        initialTab:
          previous.initialTab ??
          (next.initialPath.length > 0 ? next.initialTab : undefined),
      });
      if (href === previousHref) return;
      const state = window.history.state ?? {};
      const existing = getScreenRouteState(state);
      const persistedOwnerId = routeOwnerId.current;
      const baseHref =
        existing?.ownerId === persistedOwnerId
          ? existing.baseHref
          : `${window.location.pathname}${window.location.search}`;
      const baselineHref =
        window.location.pathname === "/" ? baseHref : previousHref;
      const routes =
        existing?.ownerId === persistedOwnerId
          ? existing.routes
          : [baselineHref];
      const isAncestor =
        next.initialTab === previous.initialTab &&
        next.initialPath.length < previous.initialPath.length &&
        next.initialPath.every(
          (part, index) => part === previous.initialPath[index],
        );
      let ancestorIndex = isAncestor ? routes.lastIndexOf(href) : -1;
      const initialLocation = readLocation(componentName, componentProps);
      if (
        isAncestor &&
        ancestorIndex < 0 &&
        new URL(routes[0] ?? "/", window.location.origin).pathname === "/" &&
        getHref({
          ...initialLocation,
          initialTab: initialLocation.initialTab ?? next.initialTab,
        }) === href
      ) {
        ancestorIndex = 0;
      }
      if (
        !options?.replace &&
        ancestorIndex >= 0 &&
        ancestorIndex < routes.length - 1
      ) {
        window.history.go(ancestorIndex - routes.length + 1);
        return;
      }
      currentLocation.current = next;
      setLocation(next);
      if (isAncestor || options?.replace) {
        // Route corrections and direct deep links replace the current history entry.
        const nextRoutes = [...routes.slice(0, -1), href];
        window.history.replaceState(
          {
            ...state,
            [screenRouteKey]: {
              ownerId: persistedOwnerId,
              routes: nextRoutes,
              baseHref,
            },
          },
          "",
          href,
        );
        return;
      }
      window.history.replaceState(
        {
          ...state,
          [screenRouteKey]: { ownerId: persistedOwnerId, routes, baseHref },
        },
        "",
        routes.at(-1),
      );
      window.history.pushState(
        {
          ...state,
          [screenRouteKey]: {
            ownerId: persistedOwnerId,
            routes: [...routes, href],
            baseHref,
          },
        },
        "",
        href,
      );
    },
    [componentProps, getHref, supportsRouting, trackHistory],
  );

  const value = useMemo(
    () => (supportsRouting ? { ownerId, location, navigate } : null),
    [ownerId, location, navigate, supportsRouting],
  );
  return (
    <ScreenRouteContext.Provider value={value}>
      {children}
    </ScreenRouteContext.Provider>
  );
}
